import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslateText } from "../api/hooks";
import { detectIncomingTranslateTarget } from "../utils/detect-incoming-language";
import { TRANSLATE_TEXT_MAX_LENGTH } from "../types";
import type { TranslationLanguage } from "../types";

const SYNC_DEBOUNCE_MS = 300;

interface UseInPlaceTranslationArgs {
  getCurrentText: () => string;
  applyText: (next: string) => void;
  getSnapshot?: () => string;
  formatResult?: (translated: string) => string;
  // Email-only: when set, translate()/undo() also handle the subject line.
  getSubjectText?: () => string;
  applySubjectText?: (next: string) => void;
}

const opposite = (language: TranslationLanguage): TranslationLanguage =>
  language === "en" ? "es" : "en";

// Editable translate-with-undo state machine shared by the composer and the
// AI suggested-reply card (CRM-1092). The caller owns the text surface via
// getCurrentText/applyText so this hook is agnostic to editor vs. textarea.
export const useInPlaceTranslation = ({
  getCurrentText,
  applyText,
  getSnapshot,
  formatResult,
  getSubjectText,
  applySubjectText
}: UseInPlaceTranslationArgs) => {
  const { mutate, isPending: isTranslating } = useTranslateText();
  const [targetLanguage, setTargetLanguage] =
    useState<TranslationLanguage>("es");
  const [undoSnapshot, setUndoSnapshot] = useState<string | null>(null);
  const [subjectUndoSnapshot, setSubjectUndoSnapshot] = useState<string | null>(
    null
  );
  const generationRef = useRef(0);
  const syncRef = useRef(0);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(syncTimeoutRef.current), []);

  // Matches the button label to what's actually typed; debounced, and a
  // no-op when the detector isn't confident (keeps the current direction).
  const syncTargetLanguage = useCallback((text: string) => {
    clearTimeout(syncTimeoutRef.current);
    const version = ++syncRef.current;
    syncTimeoutRef.current = setTimeout(() => {
      detectIncomingTranslateTarget(text).then((detected) => {
        if (detected && version === syncRef.current)
          setTargetLanguage(detected);
      });
    }, SYNC_DEBOUNCE_MS);
  }, []);

  // Invalidates any sync scheduled by the last keystroke so it can't fire
  // ~SYNC_DEBOUNCE_MS later and clobber the direction translate()/undo() set.
  const cancelPendingSync = useCallback(() => {
    clearTimeout(syncTimeoutRef.current);
    syncRef.current += 1;
  }, []);

  const translate = useCallback(() => {
    const text = getCurrentText();
    const trimmedLength = text.trim().length;
    if (!trimmedLength) return;
    if (trimmedLength > TRANSLATE_TEXT_MAX_LENGTH) {
      toast.error(
        `Message is too long to translate (max ${TRANSLATE_TEXT_MAX_LENGTH.toLocaleString()} characters).`
      );
      return;
    }
    cancelPendingSync();
    const snapshot = getSnapshot ? getSnapshot() : text;
    const subjectText = getSubjectText ? getSubjectText() : "";
    const subjectSnapshot = subjectText;
    const generation = generationRef.current;

    mutate(
      {
        text,
        targetLanguage,
        ...(subjectText.trim() && { subject: subjectText })
      },
      {
        onSuccess: ({ translatedText, translatedSubject }) => {
          if (generation !== generationRef.current) return;
          setUndoSnapshot(snapshot);
          applyText(
            formatResult ? formatResult(translatedText) : translatedText
          );
          if (getSubjectText && applySubjectText) {
            setSubjectUndoSnapshot(subjectSnapshot);
            if (typeof translatedSubject === "string")
              applySubjectText(translatedSubject);
          }
          setTargetLanguage(opposite(targetLanguage));
        },
        onError: () => {
          if (generation !== generationRef.current) return;
          toast.error("We couldn't translate your message. Try again.");
        }
      }
    );
  }, [
    applyText,
    applySubjectText,
    cancelPendingSync,
    formatResult,
    getCurrentText,
    getSnapshot,
    getSubjectText,
    targetLanguage,
    mutate
  ]);

  const undo = useCallback(() => {
    if (undoSnapshot === null) return;
    cancelPendingSync();
    applyText(undoSnapshot);
    if (applySubjectText && subjectUndoSnapshot !== null)
      applySubjectText(subjectUndoSnapshot);
    setUndoSnapshot(null);
    setSubjectUndoSnapshot(null);
    setTargetLanguage((current) => opposite(current));
  }, [
    applySubjectText,
    applyText,
    cancelPendingSync,
    subjectUndoSnapshot,
    undoSnapshot
  ]);

  // Voids the undo snapshot on any real edit and bumps generationRef so a
  // translate in flight can't overwrite text the user has since changed.
  const clearUndo = useCallback(() => {
    generationRef.current += 1;
    setUndoSnapshot(null);
    setSubjectUndoSnapshot(null);
  }, []);

  // Wholesale content replacement (switching destination, applying a template)
  // both voids the undo snapshot and returns the toggle to its default
  // "Translate to Spanish" direction.
  const reset = useCallback(() => {
    generationRef.current += 1;
    setUndoSnapshot(null);
    setSubjectUndoSnapshot(null);
    setTargetLanguage("es");
  }, []);

  return {
    translate,
    undo,
    clearUndo,
    reset,
    syncTargetLanguage,
    canUndo: undoSnapshot !== null,
    targetLanguage,
    isTranslating
  };
};
